import React, { useState, useContext } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { Link } from "gatsby";

import GlobalContext from "../../context/GlobalContext";
import Offcanvas from "../Offcanvas";
import NestedMenu from "../NestedMenu";
import ProfileAvatar from "../ProfileAvatar";
import XpBadge from "../../sections/gamification/XpBadge";
import StreakFlame from "../../sections/gamification/StreakFlame";
import NewsletterButton from "../../sections/newsletter/NewsletterButton";
import SlackButton from "../../sections/community/SlackButton";
import { device } from "../../utils";
import Logo from "../Logo";
import { menuItems } from "./menuItems";

const SiteHeader = styled.header`
  padding: 0;
  position: absolute !important;
  top: 0;
  right: 0;
  width: 100%;
  z-index: 999;
  background: var(--nd-black);
  border-bottom: 1px solid var(--nd-border);
  /* Keep the whole nav row on ONE line — Bootstrap .navbar defaults to
     flex-wrap: wrap, which dropped the hamburger to a second line once the
     mobile gamification chrome was present. */
  .site-navbar { flex-wrap: nowrap; }
  @media ${device.lg} {
    position: fixed !important;
    transition: 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
    &.scrolling {
      transform: translateY(-100%);
      transition: 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
    }
    &.reveal-header {
      transform: translateY(0%);
      z-index: 9999;
      background: var(--nd-black);
      border-bottom: 1px solid var(--nd-border);
    }
  }
`;

const ToggleButton = styled.button`
  color: var(--nd-text-primary) !important;
  border-color: var(--nd-border-visible) !important;
  background: transparent;
`;

const Menu = styled.ul`
  @media ${device.lg} {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .dropdown-toggle {
    cursor: pointer;
  }
  > li {
    > .nav-link {
      @media ${device.lg} {
        color: var(--nd-text-disabled) !important;
        transition: 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
        align-items: center;
        display: inline-flex;
        font-family: 'Space Mono', monospace;
        font-size: 11px;
        font-weight: 400;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        line-height: 24px;
        padding: 18px 12px !important;

        &.dropdown-toggle:after {
          display: none;
        }
      }
    }
    &.nav-item:hover > .nav-link,
    &.nav-item.active > .nav-link {
      color: var(--nd-text-display) !important;
    }
  }
  .nav-item.dropdown {
    @media ${device.lg} {
      position: relative;
      z-index: 99;
    }
    &:hover {
      > .menu-dropdown {
        @media ${device.lg} {
          top: 90%;
          right: 0;
          opacity: 1;
          pointer-events: visible;
        }
      }
    }
  }
`;

const MenuDropdown = styled.ul`
  list-style: none;
  @media ${device.lg} {
    top: 110%;
    position: absolute;
    min-width: 200px;
    max-width: 200px;

    background-color: var(--nd-surface);
    padding: 8px 0;
    z-index: 99;
    opacity: 0;
    transition: opacity 250ms cubic-bezier(0.25, 0.1, 0.25, 1), top 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
    pointer-events: none;
    left: -90%;
    border: 1px solid var(--nd-border-visible);
    border-radius: 8px;
    display: block;
    border-top: none;
  }
  > .drop-menu-item {
    color: var(--nd-text-secondary);
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 8px 20px;

    a {
      color: var(--nd-text-secondary) !important;
      transition: color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
      position: relative;
      display: flex;
      align-items: center;
      font-weight: 400;
      text-transform: uppercase;
      &.dropdown-toggle::after {
        display: none;
      }
    }

    &:hover,
    &.active {
      > a {
        color: var(--nd-text-display) !important;
        text-decoration: none;
      }
    }
    &.dropdown {
      position: relative;

      &:hover {
        > .menu-dropdown {
          @media ${device.lg} {
            top: 10px;
            opacity: 1;
            pointer-events: visible;
            transform: translateX(-100%);
          }
        }
      }
      > .menu-dropdown {
        border-top-color: var(--nd-border-visible);
        @media ${device.lg} {
          top: 10px;
          left: 0%;
          opacity: 0;
          transform: translateX(-110%);
          transition: 0.4s;
          pointer-events: none;
        }
        > .drop-menu-item {
          @media ${device.lg} {
            padding-left: 30px;
            padding-right: 30px;
          }
        }
      }
    }
  }
  &.dropdown-right {
    left: auto;
    right: -90%;
  }
`;

const Header = ({ isDark }) => {
  const gContext = useContext(GlobalContext);
  const [showScrolling, setShowScrolling] = useState(false);
  const [showReveal, setShowReveal] = useState(false);

  useScrollPosition(({ prevPos, currPos }) => {
    if (currPos.y < 0) {
      setShowScrolling(true);
    } else {
      setShowScrolling(false);
    }
    if (currPos.y < -300) {
      setShowReveal(true);
    } else {
      setShowReveal(false);
    }
  });

  return (
    <>
      <SiteHeader
        className={`sticky-header ${showScrolling ? "scrolling" : ""} ${
          showReveal ? "reveal-header" : ""
        }`}
        dark={isDark ? 1 : 0}
      >
        <Container fluid>
          <nav className="navbar px-0 px-md-3 site-navbar offcanvas-active navbar-expand-lg navbar-light">
            {/* <!-- Brand Logo--> */}
            <div className="brand-logo">
              <Logo color={isDark ? "light" : "dark"} />
            </div>
            <div className="collapse navbar-collapse">
              <div className="navbar-nav ml-lg-auto mr-3">
                <Menu
                  className="navbar-nav d-none d-lg-flex"
                  dark={isDark ? 1 : 0}
                >
                  {menuItems.map(
                    (
                      { label, isExternal = false, name, items, ...rest },
                      index
                    ) => {
                      const hasSubItems = Array.isArray(items);
                      return (
                        <React.Fragment key={name + index}>
                          {hasSubItems ? (
                            <li className="nav-item dropdown" {...rest}>
                              <a
                                className="nav-link dropdown-toggle"
                                role="button"
                                data-toggle="dropdown"
                                aria-expanded="false"
                                href="/#"
                                onClick={(e) => e.preventDefault()}
                              >
                                {label}
                              </a>
                              <MenuDropdown
                                className="menu-dropdown dropdown-right"
                                dark={isDark ? 1 : 0}
                              >
                                {items.map((subItem, indexSub) => {
                                  const hasInnerSubItems = Array.isArray(
                                    subItem.items
                                  );
                                  return (
                                    <React.Fragment
                                      key={subItem.name + indexSub}
                                    >
                                      {hasInnerSubItems ? (
                                        <li className="drop-menu-item dropdown">
                                          <a
                                            className="dropdown-toggle"
                                            role="button"
                                            data-toggle="dropdown"
                                            aria-expanded="false"
                                            href="/#"
                                            onClick={(e) => e.preventDefault()}
                                          >
                                            {subItem.label}
                                          </a>
                                          <MenuDropdown
                                            className="menu-dropdown dropdown-right"
                                            dark={isDark ? 1 : 0}
                                          >
                                            {subItem.items.map(
                                              (itemSubInner, indexSubInner) => {
                                                const hasInnerMostItems = Array.isArray(
                                                  itemSubInner.items
                                                );
                                                return (
                                                  <React.Fragment
                                                    key={
                                                      itemSubInner.name +
                                                      indexSubInner
                                                    }
                                                  >
                                                    {hasInnerMostItems ? (
                                                      <li className="drop-menu-item dropdown">
                                                        <a
                                                          className="dropdown-toggle"
                                                          role="button"
                                                          data-toggle="dropdown"
                                                          aria-expanded="false"
                                                          href="/#"
                                                          onClick={(e) =>
                                                            e.preventDefault()
                                                          }
                                                        >
                                                          {itemSubInner.label}
                                                        </a>
                                                        <MenuDropdown
                                                          className="menu-dropdown dropdown-right"
                                                          dark={isDark ? 1 : 0}
                                                        >
                                                          {itemSubInner.items.map(
                                                            (
                                                              itemLast,
                                                              indexLast
                                                            ) => (
                                                              <li
                                                                className="drop-menu-item"
                                                                key={
                                                                  itemLast.name +
                                                                  indexLast
                                                                }
                                                              >
                                                                {itemLast.isExternal ? (
                                                                  <a
                                                                    href={`${itemLast.name}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                  >
                                                                    {
                                                                      itemLast.label
                                                                    }
                                                                  </a>
                                                                ) : (
                                                                  <Link
                                                                    to={`/${itemLast.name}`}
                                                                  >
                                                                    {
                                                                      itemLast.label
                                                                    }
                                                                  </Link>
                                                                )}
                                                              </li>
                                                            )
                                                          )}
                                                        </MenuDropdown>
                                                      </li>
                                                    ) : (
                                                      <li className="drop-menu-item">
                                                        {itemSubInner.isExternal ? (
                                                          <a
                                                            href={`${itemSubInner.name}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                          >
                                                            {itemSubInner.label}
                                                          </a>
                                                        ) : (
                                                          <Link
                                                            to={`/${itemSubInner.name}`}
                                                          >
                                                            {itemSubInner.label}
                                                          </Link>
                                                        )}
                                                      </li>
                                                    )}
                                                  </React.Fragment>
                                                );
                                              }
                                            )}
                                          </MenuDropdown>
                                        </li>
                                      ) : (
                                        <li className="drop-menu-item">
                                          {subItem.isExternal ? (
                                            <a
                                              href={`${subItem.name}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {subItem.label}
                                            </a>
                                          ) : (
                                            <Link to={`/${subItem.name}`}>
                                              {subItem.label}
                                            </Link>
                                          )}
                                        </li>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </MenuDropdown>
                            </li>
                          ) : (
                            <li className="nav-item" {...rest}>
                              {isExternal ? (
                                <a
                                  className="nav-link"
                                  href={`${name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {label}
                                </a>
                              ) : (
                                <Link
                                  className="nav-link"
                                  to={`/${name}`}
                                  role="button"
                                  aria-expanded="false"
                                >
                                  {label}
                                </Link>
                              )}
                            </li>
                          )}
                        </React.Fragment>
                      );
                    }
                  )}

                  {/* XP badge (visible when signed in) */}
                  <li className="nav-item d-flex align-items-center ml-2">
                    <XpBadge />
                  </li>

                  <li className="nav-item d-flex align-items-center ml-2">
                    <StreakFlame />
                  </li>

                  <li className="nav-item d-flex align-items-center ml-2">
                    <NewsletterButton />
                  </li>

                  <li className="nav-item d-flex align-items-center ml-2">
                    <SlackButton />
                  </li>

                  {/* Profile avatar (sign-in / dropdown) */}
                  <li className="nav-item d-flex align-items-center ml-2">
                    <ProfileAvatar />
                  </li>

                  {/* Book a Call CTA */}
                  <li className="nav-item">
                    <a
                      className="nav-link"
                      href="/#"
                      onClick={(e) => {
                        e.preventDefault();
                        gContext.toggleContact();
                      }}
                      style={{
                        background: "#FFFFFF",
                        color: "#000000",
                        borderRadius: "999px",
                        padding: "8px 20px",
                        marginLeft: "10px",
                        fontSize: "11px",
                        fontWeight: "400",
                        fontFamily: "'Space Mono', monospace",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      BOOK A CALL
                    </a>
                  </li>
                </Menu>
              </div>
            </div>

            {/* Mobile-only chrome strip — gamification components render null
                outside /labs + /academy so they don't pollute other pages.
                Use small gap + flex-shrink-safe wrapper so the chrome can
                compress before crowding out the hamburger toggle. */}
            <div
              className="d-flex d-lg-none align-items-center ml-auto mr-1"
              style={{ gap: 4, minWidth: 0 }}
            >
              <XpBadge />
              <StreakFlame />
              <NewsletterButton />
              <SlackButton />
              <ProfileAvatar />
            </div>

            <ToggleButton
              className={`navbar-toggler btn-close-off-canvas ml-2 ${
                gContext.visibleOffCanvas ? "collapsed" : ""
              }`}
              type="button"
              data-toggle="collapse"
              data-target="#mobile-menu"
              aria-controls="mobile-menu"
              aria-expanded="false"
              aria-label="Toggle navigation"
              onClick={gContext.toggleOffCanvas}
              dark={isDark ? 1 : 0}
            >
              {/* <i className="icon icon-simple-remove icon-close"></i> */}
              <i className="icon icon-menu-34 icon-burger d-block"></i>
            </ToggleButton>
          </nav>
        </Container>
      </SiteHeader>
      <Offcanvas
        show={gContext.visibleOffCanvas}
        onHideOffcanvas={gContext.toggleOffCanvas}
      >
        <NestedMenu menuItems={menuItems} />
      </Offcanvas>
    </>
  );
};
export default Header;
